import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { DollarSign, FileText, Calendar } from 'lucide-react';
import type { AccountPayableFormData } from '@/types/accounts-payable.types';

interface AccountPayableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (accountData: AccountPayableFormData) => void;
}

export const AccountPayableModal: React.FC<AccountPayableModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<AccountPayableFormData>({
    supplierId: '',
    originalAmount: 0,
    dueDate: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock suppliers - en un entorno real esto vendría de una API
  const mockSuppliers = [
    { id: 'sup-001', name: 'Proveedor Z' },
    { id: 'sup-002', name: 'Distribuidora San Miguel' },
    { id: 'sup-003', name: 'Carnes Premium S.A.' },
    { id: 'sup-004', name: 'Verduras Frescas Ltda.' }
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierId) {
      newErrors.supplierId = 'Debe seleccionar un proveedor';
    }

    if (!formData.originalAmount || formData.originalAmount <= 0) {
      newErrors.originalAmount = 'El monto debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      
      // Reset form
      setFormData({
        supplierId: '',
        originalAmount: 0,
        dueDate: '',
        notes: ''
      });
      setErrors({});
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (value: string) => {
    // Solo permitir números y punto decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({ ...prev, originalAmount: parseFloat(numericValue) || 0 }));
    
    // Limpiar error de monto si existe
    if (errors.originalAmount) {
      setErrors(prev => ({ ...prev, originalAmount: '' }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Nueva Cuenta por Pagar
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Proveedor */}
          <div className="space-y-2">
            <Label htmlFor="supplierId">
              Proveedor <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.supplierId} 
              onValueChange={(value) => {
                setFormData(prev => ({ ...prev, supplierId: value }));
                if (errors.supplierId) {
                  setErrors(prev => ({ ...prev, supplierId: '' }));
                }
              }}
            >
              <SelectTrigger className={errors.supplierId ? 'border-red-500' : ''}>
                <SelectValue placeholder="Seleccionar proveedor" />
              </SelectTrigger>
              <SelectContent>
                {mockSuppliers.map(supplier => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.supplierId && (
              <p className="text-sm text-red-500">{errors.supplierId}</p>
            )}
          </div>

          {/* Monto */}
          <div className="space-y-2">
            <Label htmlFor="originalAmount">
              Monto Original <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="originalAmount"
                placeholder="0.00"
                value={formData.originalAmount > 0 ? formData.originalAmount.toString() : ''}
                onChange={(e) => handleAmountChange(e.target.value)}
                className={`pl-10 ${errors.originalAmount ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.originalAmount && (
              <p className="text-sm text-red-500">{errors.originalAmount}</p>
            )}
          </div>

          {/* Fecha de vencimiento */}
          <div className="space-y-2">
            <Label htmlFor="dueDate">
              Fecha de Vencimiento
            </Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-500">
              Opcional. Si no se especifica, no tendrá fecha de vencimiento.
            </p>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              placeholder="Información adicional sobre la cuenta por pagar..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !formData.supplierId || formData.originalAmount <= 0}
          >
            {isSubmitting ? 'Creando...' : 'Crear Cuenta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};